import { DigitalUserAccounts } from "./gateway/graphql-types/DigitalUserAccounts";
import {
  DigitalAccountStatus,
  DigitalAccountType,
} from "./gateway/graphql-types/globalTypes";
import {
  PROFILE_FIELD_MAP_RESULT_UNSUPPORTED,
  ProfileFieldMapHook,
} from "./profileFieldMap";
import { useKrakenProfileFieldMap } from "./mappings/kraken/krakenProfileFieldMap";
import { useLpgProfileFieldMap } from "./mappings/lpg/lpgProfileFieldMap";
import { useMultiCrmsProfileFieldMap } from "./mappings/multi/multiCrmsProfileFieldMap";

export enum ProfileFieldMapTypes {
  KRAKEN = "KRAKEN",
  LPG = "LPG",
  MULTI_CRMS = "MULTI_CRMS",
  NONE = "NONE",
  BROADBAND = "BROADBAND",
}

export const isBroadbandOnlyCustomer = (type: ProfileFieldMapTypes) =>
  type === ProfileFieldMapTypes.BROADBAND;

const PROFILE_FIELD_MAP_HOOKS: {
  [key in ProfileFieldMapTypes]: ProfileFieldMapHook;
} = {
  [ProfileFieldMapTypes.KRAKEN]: useKrakenProfileFieldMap,
  [ProfileFieldMapTypes.LPG]: useLpgProfileFieldMap,
  [ProfileFieldMapTypes.MULTI_CRMS]: useMultiCrmsProfileFieldMap,
  [ProfileFieldMapTypes.BROADBAND]: PROFILE_FIELD_MAP_RESULT_UNSUPPORTED,
  [ProfileFieldMapTypes.NONE]: PROFILE_FIELD_MAP_RESULT_UNSUPPORTED,
};

function determineProfileFieldMapType(gatewayData: DigitalUserAccounts) {
  const accountTypes = gatewayData.viewer.digital.accounts.map(
    (account) => account.type
  );

  const activeAccountTypes = gatewayData.viewer.digital.accounts
    .filter((account) => account.status === DigitalAccountStatus.ACTIVE)
    .map((account) => account.type);

  if (accountTypes.length === 0) {
    throw new Error("The user has no accounts.");
  }

  const hasKraken = accountTypes.some(
    (type) => type === DigitalAccountType.KRAKEN
  );
  const hasLpg = gatewayData.viewer.digital.accounts
    .filter((account) => account.status !== DigitalAccountStatus.INCOMPLETE)
    .some((account) => account.type === DigitalAccountType.LPG);

  if (hasKraken && hasLpg) {
    return ProfileFieldMapTypes.MULTI_CRMS;
  }
  if (hasKraken) {
    return ProfileFieldMapTypes.KRAKEN;
  }
  if (hasLpg) {
    return ProfileFieldMapTypes.LPG;
  }

  // If the user only has SUPERLOOP accounts, but none of them are active,
  // throw a specific error to highlight the absence of active SUPERLOOP accounts.
  if (
    activeAccountTypes.length === 0 &&
    accountTypes.every((type) => type === DigitalAccountType.SUPERLOOP)
  ) {
    throw new Error("The user has no active superloop accounts.");
  }

  // If all active accounts are SUPERLOOP, assign the BROADBAND map type.
  // Inactive accounts of other types are ignored.
  if (
    activeAccountTypes.length > 0 &&
    activeAccountTypes.every((type) => type === DigitalAccountType.SUPERLOOP)
  ) {
    return ProfileFieldMapTypes.BROADBAND;
  }

  throw new Error(
    `The user has accounts that do not match any precedence rules [${accountTypes}]`
  );
}

export function determineProfileFieldMapping(
  gatewayData: DigitalUserAccounts
): ProfileFieldMapHook {
  const profileFieldMapType = determineProfileFieldMapType(gatewayData);
  return PROFILE_FIELD_MAP_HOOKS[profileFieldMapType];
}

export type ProfileFieldMapMetadata = {
  type: ProfileFieldMapTypes;
  hasLPG: boolean;
  hasLPGOnly: boolean;
  hasActiveBroadband: boolean;
};

export const PROFILE_FIELD_MAP_METADATA_DEFAULT: ProfileFieldMapMetadata = {
  type: ProfileFieldMapTypes.NONE,
  hasLPG: false,
  hasLPGOnly: false,
  hasActiveBroadband: false,
};

export function determineProfileFieldMapMetadata(
  gatewayData: DigitalUserAccounts
) {
  const accounts = gatewayData.viewer.digital.accounts;
  return {
    type: determineProfileFieldMapType(gatewayData),
    hasLPGOnly: accounts
      .filter(({ type }) => type !== DigitalAccountType.SAP)
      .every(({ type }) => type === DigitalAccountType.LPG),
    hasLPG: accounts.some(({ type }) => type === DigitalAccountType.LPG),
    hasActiveBroadband: accounts.some(
      ({ type, status }) =>
        type === DigitalAccountType.SUPERLOOP &&
        status === DigitalAccountStatus.ACTIVE
    ),
  };
}
